import { useState, useEffect } from "react";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Clock, CheckCircle, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const counties = ["Sonoma", "Napa", "Marin", "Lake", "Mendocino"];
const jobTypes = ["Full-Time", "Part-Time", "Internship", "Apprenticeship"];

export default function EmployerDashboard({ user }) {
  const [myJobs, setMyJobs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [jobForm, setJobForm] = useState({
    title: "",
    company: "",
    county: "Sonoma",
    city: "",
    job_type: "Full-Time",
    trade_type: "",
    description: "",
    skills: "",
    pay_range: "",
    apply_info: ""
  });

  useEffect(() => {
    fetchMyJobs();
  }, []);

  const fetchMyJobs = async () => {
    try {
      const token = localStorage.getItem("token");
      // Get all jobs and filter by submitted_by
      const response = await axios.get(`${API}/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Note: In production, create a dedicated endpoint for employer's jobs
      setMyJobs(response.data.filter(job => job.submitted_by === user.id));
    } catch (error) {
      console.error("Failed to fetch jobs", error);
    }
  };

  const handleSubmitJob = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/jobs/submit`, jobForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Job submitted for admin review!");
      setDialogOpen(false);
      setJobForm({
        title: "",
        company: "",
        county: "Sonoma",
        city: "",
        job_type: "Full-Time",
        trade_type: "",
        description: "",
        skills: "",
        pay_range: "",
        apply_info: ""
      });
      fetchMyJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit job");
    }
  };

  const getStatusBadge = (status) => {
    if (status === "pending") {
      return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="h-3 w-3 mr-1" />Pending Review</Badge>;
    } else if (status === "approved") {
      return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
    } else if (status === "rejected") {
      return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
    }
    return null;
  };

  const pendingJobs = myJobs.filter(j => j.status === "pending");
  const approvedJobs = myJobs.filter(j => j.status === "approved");
  const rejectedJobs = myJobs.filter(j => j.status === "rejected");

  return (
    <div className="space-y-6" data-testid="employer-dashboard">
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="flex justify-between items-start gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk', color: '#FF6B6B' }}>
              Employer Dashboard
            </h1>
            <p className="text-gray-600">Post job opportunities for review</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="submit-job-button"
                style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)', color: 'white' }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Submit Job Posting
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Submit New Job Posting</DialogTitle>
                <p className="text-sm text-gray-600">Your job will be reviewed by an admin before going live</p>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Job Title*</Label>
                    <Input value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} data-testid="job-title-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Company*</Label>
                    <Input value={jobForm.company} onChange={(e) => setJobForm({ ...jobForm, company: e.target.value })} data-testid="job-company-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>County*</Label>
                    <Select value={jobForm.county} onValueChange={(value) => setJobForm({ ...jobForm, county: value })}>
                      <SelectTrigger data-testid="job-county-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{counties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>City*</Label>
                    <Input value={jobForm.city} onChange={(e) => setJobForm({ ...jobForm, city: e.target.value })} data-testid="job-city-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Job Type*</Label>
                    <Select value={jobForm.job_type} onValueChange={(value) => setJobForm({ ...jobForm, job_type: value })}>
                      <SelectTrigger data-testid="job-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{jobTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Trade Type*</Label>
                    <Input value={jobForm.trade_type} onChange={(e) => setJobForm({ ...jobForm, trade_type: e.target.value })} placeholder="e.g., Electrical" data-testid="job-trade-type-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Pay Range</Label>
                  <Input value={jobForm.pay_range} onChange={(e) => setJobForm({ ...jobForm, pay_range: e.target.value })} placeholder="e.g., $20-25/hr" data-testid="job-pay-range-input" />
                </div>
                <div className="space-y-2">
                  <Label>Description*</Label>
                  <Textarea value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })} rows={4} data-testid="job-description-input" />
                </div>
                <div className="space-y-2">
                  <Label>Required Skills*</Label>
                  <Textarea value={jobForm.skills} onChange={(e) => setJobForm({ ...jobForm, skills: e.target.value })} rows={3} data-testid="job-skills-input" />
                </div>
                <div className="space-y-2">
                  <Label>How to Apply*</Label>
                  <Textarea value={jobForm.apply_info} onChange={(e) => setJobForm({ ...jobForm, apply_info: e.target.value })} rows={2} placeholder="Email, phone, or website" data-testid="job-apply-info-input" />
                </div>
                <Button onClick={handleSubmitJob} className="w-full" data-testid="submit-job-form-button" style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)', color: 'white' }}>
                  Submit for Review
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pending">Pending ({pendingJobs.length})</TabsTrigger>
          <TabsTrigger value="approved">Approved ({approvedJobs.length})</TabsTrigger>
          <TabsTrigger value="rejected">Rejected ({rejectedJobs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          {pendingJobs.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">No pending job postings</p>
              </CardContent>
            </Card>
          ) : (
            pendingJobs.map((job) => (
              <Card key={job.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl">{job.title}</CardTitle>
                      <CardDescription>{job.company} - {job.city}, {job.county}</CardDescription>
                    </div>
                    {getStatusBadge(job.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-2">{job.description.substring(0, 150)}...</p>
                  <div className="flex gap-2 text-sm text-gray-500">
                    <Badge variant="outline">{job.job_type}</Badge>
                    <Badge variant="outline">{job.trade_type}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="approved" className="space-y-4">
          {approvedJobs.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <CheckCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">No approved job postings yet</p>
              </CardContent>
            </Card>
          ) : (
            approvedJobs.map((job) => (
              <Card key={job.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl">{job.title}</CardTitle>
                      <CardDescription>{job.company} - {job.city}, {job.county}</CardDescription>
                    </div>
                    {getStatusBadge(job.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-2">{job.description.substring(0, 150)}...</p>
                  <div className="flex gap-2 text-sm text-gray-500">
                    <Badge variant="outline">{job.job_type}</Badge>
                    <Badge variant="outline">{job.trade_type}</Badge>
                    {job.pay_range && <Badge variant="outline">{job.pay_range}</Badge>}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="rejected" className="space-y-4">
          {rejectedJobs.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <XCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">No rejected job postings</p>
              </CardContent>
            </Card>
          ) : (
            rejectedJobs.map((job) => (
              <Card key={job.id} className="border-red-200">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl">{job.title}</CardTitle>
                      <CardDescription>{job.company} - {job.city}, {job.county}</CardDescription>
                    </div>
                    {getStatusBadge(job.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-2">{job.description.substring(0, 150)}...</p>
                  <p className="text-sm text-red-600 mt-2">This posting did not meet our guidelines. Please review our posting requirements and try again.</p>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
